import styles from './edit-place-dialog.module.scss';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useImmer } from 'use-immer';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../../requestUtils';
import { Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { IRoom } from '../../../../_models/room';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface EditPlaceDialogProps {
  onClose: () => void,
  open: boolean,
  refreshData: () => void
  roomId: string
}

export function EditPlaceDialog(props: EditPlaceDialogProps) {
  const { data }: { data: IRoom } = useCourseGetRequestHook(`room/get/${props.roomId}`, undefined);
  const [room, updateRoom] = useImmer(data);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => updateRoom(draft => { draft.name = event.target.value });
  const handleSeatsChange = (event: ChangeEvent<HTMLSelectElement>) => updateRoom(draft => { draft.seats = parseInt(event.target.value) });
  const handlePlaceChange = (event: ChangeEvent<HTMLInputElement>) => updateRoom(draft => { draft.place = event.target.value });
  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => updateRoom(draft => { draft.address = event.target.value });
  const handlePostCodeChange = (event: ChangeEvent<HTMLInputElement>) => updateRoom(draft => { draft.postCode = event.target.value });
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => updateRoom(draft => { draft.city = event.target.value });

  useEffect(() => {
    if (data) {
      updateRoom(data);
    }
  }, [data]);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      room?.name &&
      room?.seats &&
      room?.address &&
      room?.city;
    return requiredFieldsFilled ? false : true;
  };

  const handleUpdatePlace = () => {
    setLoading(true);
    makeCourseRequest(`room/upsert`, 'POST', room).then(result => {
      setSnackbarMessage({ content: t('editPlace.updatedPlace'), type: "info", });
      setLoading(false);
      props.refreshData();
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('editPlace.couldNotUpdatePlace'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('editPlace.changeLocation')}
      </DialogTitle>
      <DialogContent>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.name')} &#42;</span>}
            name='name'
            fullWidth
            sx={{ minWidth: 300 }}
            value={room?.name || ''}
            onChange={handleNameChange}
          />
        </div>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.seats')} &#42;</span>}
                name='seats'
                type='number'
                sx={{ minWidth: 100 }}
                value={room?.seats || null}
                onChange={handleSeatsChange}
              />
            </div>
          </Col>
        </Row>
        <div className='mb-3'>
          <TextField
            label={t('standard.place')}
            name='place'
            fullWidth
            sx={{ minWidth: 300 }}
            value={room?.place || ''}
            onChange={handlePlaceChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.address')} &#42;</span>}
            name='address'
            fullWidth
            sx={{ minWidth: 300 }}
            value={room?.address || ''}
            onChange={handleAddressChange}
          />
        </div>

        <Row>
          <Col xl={4}>
            <div className='mb-3'>
              <TextField
                label={t('standard.postCode')}
                name='postCode'
                sx={{ minWidth: 100 }}
                value={room?.postCode || ''}
                onChange={handlePostCodeChange}
              />
            </div>
          </Col>
          <Col xl={8}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.city')} &#42;</span>}
                fullWidth
                name='city'
                sx={{ minWidth: 300 }}
                value={room?.city || ''}
                onChange={handleCityChange}
              />
            </div>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleUpdatePlace}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('editPlace.updateLocation')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditPlaceDialog;
